import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import {
    BLOCKS,
    Document as RichTextDocument,
    INLINES,
    MARKS,
} from "@contentful/rich-text-types";
import { useTheme } from "@emotion/react";
import React, { ReactElement, useContext } from "react";
import { Text, TextStyle, View, ViewStyle, Platform } from "react-native";
import { useOpenUrl, UrlType } from "myplan/utils/url";
import { LogicStateContext } from "../../utils/quiz";
import { getTextStyles } from "../../utils/styles";
import EmbeddedImage from "./components/EmbeddedImage";
import {
    GenderKey,
    DefaultGenderKeyByLocale,
    renderEmbededInlineTokenNode,
    TokenType,
} from "../../utils/tokenizedText";
import { LocaleContext, LocaleContextType, Locales } from "../../utils/locale";
export enum NodeDataType {
    Token = "token",
}

const useRichTextOptions = (
    theme,
    partnerGenderKey?: GenderKey,
    survivorGenderKey?: GenderKey,
    friendFamilyKey?: TokenType,
    partnerKey?: TokenType,
) => {
    const _partnerGenderKey = partnerGenderKey || GenderKey.Nonbinary;
    const _survivorGenderKey = survivorGenderKey || GenderKey.Nonbinary;
    const _friendFamilyKey = friendFamilyKey || TokenType.Friend;
    const _partnerKey = partnerKey || TokenType.Partner;
    const styles = getTextStyles(theme);
    const openUrl = useOpenUrl();
    return {
        renderMark: {
            [MARKS.UNDERLINE]: text => {
                return (
                    <Text style={styles.underline as TextStyle}>{text}</Text>
                );
            },
            [MARKS.BOLD]: (txt: string) => {
                return <Text style={styles.strong as TextStyle}>{txt}</Text>;
            },
            [MARKS.ITALIC]: text => {
                return <Text style={styles.em as TextStyle}>{text}</Text>;
            },
            [MARKS.CODE]: text => {
                return <Text style={styles.codeBlock}>{text}</Text>;
            },
        },
        renderNode: {
            // hyperlinks not enabled in rich text
            [INLINES.HYPERLINK]: (node, children) => {
                return (
                    <Text
                        style={[
                            styles.link,
                            { textDecorationLine: "underline" },
                        ]}
                        onPress={() => {
                            openUrl(UrlType.Website, {
                                websiteUrl: node.data.uri,
                            });
                        }}>
                        {children}
                    </Text>
                );
            },
            [INLINES.EMBEDDED_ENTRY]: node => {
                if (node.data._type === NodeDataType.Token) {
                    return renderEmbededInlineTokenNode(
                        node,
                        _partnerGenderKey,
                        _survivorGenderKey,
                        _friendFamilyKey,
                        _partnerKey,
                    );
                }
                return null;
            },
            [BLOCKS.EMBEDDED_ENTRY]: node => {
                return null;
            },
            [BLOCKS.PARAGRAPH]: (_node, children: Array<any>) => {
                return <Text style={styles.paragraph}>{children}</Text>;
            },
            [BLOCKS.EMBEDDED_ASSET]: node => {
                return <EmbeddedImage node={node} />;
            },
            [BLOCKS.HEADING_1]: (_node, children) => (
                <Text style={styles.heading1}>{children}</Text>
            ),
            [BLOCKS.HEADING_2]: (_node, children) => (
                <Text style={styles.heading2}>{children}</Text>
            ),
            [BLOCKS.HEADING_3]: (_node, children) => (
                <Text style={styles.heading3}>{children}</Text>
            ),
            [BLOCKS.HEADING_4]: (_node, children) => (
                <Text style={styles.heading4}>{children}</Text>
            ),
            [BLOCKS.HEADING_5]: (_node, children) => (
                <Text style={styles.heading5}>{children}</Text>
            ),
            [BLOCKS.HEADING_6]: (_node, children) => (
                <Text style={styles.heading6}>{children}</Text>
            ),
            [BLOCKS.UL_LIST]: (_node, children) => {
                if (Platform.OS === "android") {
                    return (
                        <View style={styles.list}>
                            {children.map((child, i) => {
                                return (
                                    <View
                                        key={`item-${i}`}
                                        style={styles.listItem as ViewStyle}>
                                        <Text style={styles.listItemBullet}>
                                            •
                                        </Text>
                                        {child}
                                        {/* <Text
                                            style={styles.listItemUnorderedContent}>
                                            {child}
                                        </Text> */}
                                    </View>
                                );
                            })}
                        </View>
                    );
                }
                return (
                    <View style={styles.list}>
                        {children.map((child, i) => {
                            return (
                                <View
                                    key={`item-${i}`}
                                    style={styles.listItem as ViewStyle}>
                                    <Text style={styles.listItemBullet}>•</Text>
                                    {/* {child} */}
                                    <Text
                                        style={styles.listItemUnorderedContent}>
                                        {child}
                                    </Text>
                                </View>
                            );
                        })}
                    </View>
                );
            },
            [BLOCKS.OL_LIST]: (_node, children) => {
                return (
                    <View style={styles.list}>
                        {children.map((child, i) => {
                            return (
                                <View
                                    key={`item-${i}`}
                                    style={styles.listItem as ViewStyle}>
                                    <Text style={styles.listItemNumber}>
                                        {i + 1}.
                                    </Text>
                                    <Text style={styles.listItemOrderedContent}>
                                        {child}
                                    </Text>
                                </View>
                            );
                        })}
                    </View>
                );
            },
            [BLOCKS.LIST_ITEM]: (_node, child) => {
                return <View>{child}</View>;
            },
            [BLOCKS.QUOTE]: (_node, child) => {
                return <Text style={styles.blockQuote}>{child}</Text>;
            },
            [BLOCKS.HR]: (_node, child) => {
                return null;
            },
        },
    };
};

/**
 * Creates inline tokenized rich text with miminal formatting
 * Useful for labels / titles, etc. Any place where formatted text
 * is injected into an existing Text element.
 *
 * Some block level elements (e.g. lists) are stripped, others (e.g. paragraphs) returned
 * as unformatted inline text
 *
 * We cannot assume that Contentful fields are configured correctly, so we
 * must strip out anything which would render an invalid node tree (i.e. a View within a Text)
 *
 * @param theme
 * @param partnerGenderKey
 * @returns
 */
const useInlineRichTextOptions = (
    theme,
    partnerGenderKey?: GenderKey,
    survivorGenderKey?: GenderKey,
    friendFamilyKey?: TokenType,
    partnerKey?: TokenType,
) => {
    const _partnerGenderKey = partnerGenderKey || GenderKey.Nonbinary;
    const _survivorGenderKey = survivorGenderKey || GenderKey.Nonbinary;
    const _friendFamilyKey = friendFamilyKey || TokenType.Friend;
    const _partnerKey = partnerKey || TokenType.Partner;
    const styles = getTextStyles(theme);
    const openUrl = useOpenUrl();
    return {
        renderText: (text: string) => {
            return text.replace(/\//g, "/\u200B");
        },
        renderMark: {
            [MARKS.UNDERLINE]: text => {
                return (
                    <Text style={styles.underline as TextStyle}>{text}</Text>
                );
            },
            [MARKS.BOLD]: (txt: string) => {
                return <Text style={styles.strong as TextStyle}>{txt}</Text>;
            },
            [MARKS.ITALIC]: text => {
                return <Text style={styles.em as TextStyle}>{text}</Text>;
            },
            [MARKS.CODE]: text => {
                return <Text style={styles.codeBlock}>{text}</Text>;
            },
        },
        renderNode: {
            // hyperlinks not enabled in rich text
            [INLINES.HYPERLINK]: (node, children) => {
                return (
                    <Text
                        style={[
                            styles.link,
                            { textDecorationLine: "underline" },
                        ]}
                        onPress={() => {
                            openUrl(UrlType.Website, {
                                websiteUrl: node.data.uri,
                            });
                        }}>
                        {children}
                    </Text>
                );
            },

            [INLINES.EMBEDDED_ENTRY]: node => {
                if (node.data._type === NodeDataType.Token) {
                    return renderEmbededInlineTokenNode(
                        node,
                        _partnerGenderKey,
                        _survivorGenderKey,
                        _friendFamilyKey,
                        _partnerKey,
                    );
                }
                return null;
            },
            [BLOCKS.EMBEDDED_ENTRY]: node => {
                return null;
            },
            [BLOCKS.PARAGRAPH]: (_node, children) => {
                return children;
            },
            [BLOCKS.EMBEDDED_ASSET]: node => {
                return <EmbeddedImage node={node} />;
            },
            [BLOCKS.HEADING_1]: (_node, children) => children,
            [BLOCKS.HEADING_2]: (_node, children) => children,
            [BLOCKS.HEADING_3]: (_node, children) => children,
            [BLOCKS.HEADING_4]: (_node, children) => children,
            [BLOCKS.HEADING_5]: (_node, children) => children,
            [BLOCKS.HEADING_6]: (_node, children) => children,
            [BLOCKS.UL_LIST]: (_node, children) => null,
            [BLOCKS.OL_LIST]: (_node, children) => null,
            [BLOCKS.LIST_ITEM]: (_node, child) => null,
            [BLOCKS.QUOTE]: (_node, children) => children,
            [BLOCKS.HR]: (_node, child) => {
                return null;
            },
        },
    };
};

const useHeadlineRichTextOptions = (
    theme,
    partnerGenderKey?: GenderKey,
    survivorGenderKey?: GenderKey,
    friendFamilyKey?: TokenType,
    partnerKey?: TokenType,
) => {
    const _partnerGenderKey = partnerGenderKey || GenderKey.Nonbinary;
    const _survivorGenderKey = survivorGenderKey || GenderKey.Nonbinary;
    const _friendFamilyKey = friendFamilyKey || TokenType.Friend;
    const _partnerKey = partnerKey || TokenType.Partner;
    const styles = getTextStyles(theme);
    const openUrl = useOpenUrl();
    return {
        renderText: (text: string) => {
            return text.replace(/\//g, "/\u200B");
        },
        renderMark: {
            [MARKS.UNDERLINE]: text => {
                return (
                    <Text style={styles.underline as TextStyle}>{text}</Text>
                );
            },
            [MARKS.BOLD]: (txt: string) => {
                return (
                    <Text style={styles.headlineStrong as TextStyle}>
                        {txt}
                    </Text>
                );
            },
            [MARKS.ITALIC]: text => {
                return (
                    <Text style={styles.headlineEm as TextStyle}>{text}</Text>
                );
            },
            [MARKS.CODE]: text => {
                return <Text style={styles.codeBlock}>{text}</Text>;
            },
        },
        renderNode: {
            // hyperlinks not enabled in rich text
            [INLINES.HYPERLINK]: (node, children) => {
                return (
                    <Text
                        style={[
                            styles.link,
                            { textDecorationLine: "underline" },
                        ]}
                        onPress={() => {
                            openUrl(UrlType.Website, {
                                websiteUrl: node.data.uri,
                            });
                        }}>
                        {children}
                    </Text>
                );
            },

            [INLINES.EMBEDDED_ENTRY]: node => {
                if (node.data._type === NodeDataType.Token) {
                    return renderEmbededInlineTokenNode(
                        node,
                        _partnerGenderKey,
                        _survivorGenderKey,
                        _friendFamilyKey,
                        _partnerKey,
                    );
                }
                return null;
            },
            [BLOCKS.EMBEDDED_ENTRY]: node => {
                return null;
            },
            [BLOCKS.PARAGRAPH]: (_node, children) => {
                return children;
            },
            [BLOCKS.EMBEDDED_ASSET]: node => {
                return <EmbeddedImage node={node} />;
            },
            [BLOCKS.HEADING_1]: (_node, children) => children,
            [BLOCKS.HEADING_2]: (_node, children) => children,
            [BLOCKS.HEADING_3]: (_node, children) => children,
            [BLOCKS.HEADING_4]: (_node, children) => children,
            [BLOCKS.HEADING_5]: (_node, children) => children,
            [BLOCKS.HEADING_6]: (_node, children) => children,
            [BLOCKS.UL_LIST]: (_node, children) => null,
            [BLOCKS.OL_LIST]: (_node, children) => null,
            [BLOCKS.LIST_ITEM]: (_node, child) => null,
            [BLOCKS.QUOTE]: (_node, children) => children,
            [BLOCKS.HR]: (_node, child) => {
                return null;
            },
        },
    };
};

function getGlobalFlags(logicState, locale = Locales.EN_US) {
    // normalize logic state from multiple possible sources
    let _state = {
        global: {
            ...logicState?.global,
            ...logicState?.external?.global,
            ...logicState?.internal?.global,
        },
    };

    let partnerGenderKey = DefaultGenderKeyByLocale[locale];
    let survivorGenderKey = DefaultGenderKeyByLocale[locale];
    let friendFamilyKey = TokenType.Friend;
    let partnerExKey = TokenType.Partner;

    if (_state.global.PARTNER_PRONOUN_SHE) {
        partnerGenderKey = GenderKey.Female;
    } else if (_state.global.PARTNER_PRONOUN_HE) {
        partnerGenderKey = GenderKey.Male;
    }

    if (_state.global.SURVIVOR_PRONOUN_SHE) {
        survivorGenderKey = GenderKey.Female;
    } else if (_state.global.SURVIVOR_PRONOUN_HE) {
        survivorGenderKey = GenderKey.Male;
    }

    if (_state.global.FAMILY_MEMBER) {
        friendFamilyKey = TokenType.FamilyOrMember;
    }

    if (_state.global.EX) {
        partnerExKey = TokenType.ExPartner;
    }

    return {
        partnerGenderKey,
        survivorGenderKey,
        friendFamilyKey,
        partnerExKey,
    };
}

export type RichTextProps = {
    doc: RichTextDocument;
};

export enum NodeType {
    Paragraph = "paragraph",
    Text = "text",
    EmbeddedEntryInline = "embedded-entry-inline",
}

/**
 * Block rich text for use within a View element
 *
 * Returns an array of Text and View nodes depending upon
 * text content.
 *
 * View nodes are used in constructing complex content like
 * ordered and unordered lists
 */
const RichText = ({ doc }: RichTextProps) => {
    const theme: any = useTheme();
    const { logicState: _logicState } = useContext(LogicStateContext);
    const { locale }: LocaleContextType = useContext(LocaleContext);
    const {
        partnerGenderKey,
        survivorGenderKey,
        friendFamilyKey,
        partnerExKey,
    } = getGlobalFlags(_logicState, locale);
    const options = useRichTextOptions(
        theme,
        partnerGenderKey,
        survivorGenderKey,
        friendFamilyKey,
        partnerExKey,
    );
    // bail out if no logic state
    if (!_logicState) return null;

    return documentToReactComponents(doc, options) as ReactElement<any, any>;
};

/***
 * "Inline" rich text with tokens and simple formatting
 *
 * For use within an existing React Native Text element.
 *
 * Used in body copy buttons, CTAs, etc
 */
export const InlineRichText = ({ doc }: RichTextProps) => {
    const theme: any = useTheme();
    const { logicState: _logicState } = useContext(LogicStateContext);
    const { locale }: LocaleContextType = useContext(LocaleContext);
    const {
        partnerGenderKey,
        survivorGenderKey,
        friendFamilyKey,
        partnerExKey,
    } = getGlobalFlags(_logicState, locale);
    const options = useInlineRichTextOptions(
        theme,
        partnerGenderKey,
        survivorGenderKey,
        friendFamilyKey,
        partnerExKey,
    );
    // bail out if no logic state
    if (!_logicState) return null;

    return documentToReactComponents(doc, options) as ReactElement<any, any>;
};

export const HeadlineRichText = ({ doc }: RichTextProps) => {
    const theme: any = useTheme();
    const { logicState: _logicState } = useContext(LogicStateContext);
    const { locale }: LocaleContextType = useContext(LocaleContext);
    const {
        partnerGenderKey,
        survivorGenderKey,
        friendFamilyKey,
        partnerExKey,
    } = getGlobalFlags(_logicState, locale);
    const options = useHeadlineRichTextOptions(
        theme,
        partnerGenderKey,
        survivorGenderKey,
        friendFamilyKey,
        partnerExKey,
    );
    // bail out if no logic state
    if (!_logicState) return null;

    return documentToReactComponents(doc, options) as ReactElement<any, any>;
};

export const tokenizedText = ({ doc }: RichTextProps) => {
    const { logicState: _logicState } = useContext(LogicStateContext);

    if (!_logicState) return null;
    const {
        partnerGenderKey,
        survivorGenderKey,
        friendFamilyKey,
        partnerExKey,
    } = getGlobalFlags(_logicState);

    const nodeReducer = (acc, currentNode) => {
        let result = "";
        switch (currentNode.nodeType) {
            case NodeType.Paragraph:
                result = currentNode.content.reduce(nodeReducer, "");
                break;
            case NodeType.Text:
                result = currentNode.value;
                break;
            case NodeType.EmbeddedEntryInline:
                result = "";
                if (currentNode.data._type === NodeDataType.Token) {
                    result = renderEmbededInlineTokenNode(
                        currentNode,
                        partnerGenderKey,
                        survivorGenderKey,
                        friendFamilyKey,
                        partnerExKey,
                    );
                }
                break;
        }
        return acc + result;
    };

    return doc.content.reduce(nodeReducer, "");
};

export const getRichTextLength = doc => {
    return documentToPlainTextString(doc).length;
};

export default RichText;
